import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import App from './App';
import ErrorPage from './components/ErrorPage/ErrorPage';
import Header from './components/Header/Header';
import VisitorCounter from './components/VisitorCounter/VisitorCounter';
import RouteChangeTrackerWrapper from './components/RouteChangeTrackerWrapper';
import useAuthSession from './hooks/useAuthSession';
import SpotifyAccountBoundary from './components/SpotifyPage/SpotifyAccountBoundary';
import { pasteLoader } from './components/Paste/pasteLoader';
import { cn } from '@/lib/utils';
import AgentBubble from './components/Agent/AgentBubble';
import { AgentPanelProvider } from './components/Shell/AgentPanelContext';
import { ShellProvider } from './components/Shell/ShellContext';
import { FLIGHT_VIEW_PATHS, legacyFlightsPath, type FlightView } from './components/RandomlySelected/flightPaths';

const BillSplitter = lazy(() => import('./components/BillSplitter/BillSplitter'));
const RandomlySelected = lazy(() => import('./components/RandomlySelected/RandomlySelected'));
const CrimeMap = lazy(() => import('./components/CrimeMap/CrimeMap'));
const FindEvents = lazy(() => import('./components/FindEvents/FindEvents'));
const Login = lazy(() => import('./components/Login/Login'));
const PasswordReset = lazy(() => import('./components/PasswordReset/PasswordReset'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const PromptHelper = lazy(() => import('./components/PromptHelper/PromptHelper'));
const Register = lazy(() => import('./components/Register/Register'));
const SetPlaylist = lazy(() => import('./components/SetPlaylist/SetPlaylist'));
const Spotify = lazy(() => import('./components/Spotify/Spotify'));
const SpotifyAuth = lazy(() => import('./components/SpotifyAuth/SpotifyAuth'));
const SpotifyDuplicates = lazy(() => import('./components/SpotifyDuplicates/SpotifyDuplicates'));
const TweetGallery = lazy(() => import('./components/TweetGallery/TweetGallery'));
const TwitterChat = lazy(() => import('./components/TwitterChat/TwitterChat'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail/VerifyEmail'));
const Cartinese = lazy(() => import('./components/Cartinese/Cartinese'));
const CartiChat = lazy(() => import('./components/CartiChat/CartiChat'));
const Codenames = lazy(() => import('./components/Codenames/Codenames'));
const SpotifyCountArtists = lazy(() => import('./components/SpotifyCountArtists/SpotifyCountArtists'));
const FanSince = lazy(() => import('./components/FanSince/FanSince'));
const ListeningStats = lazy(() => import('./components/ListeningStats/ListeningStats'));
const CartinesePurchase = lazy(() => import('./components/CartinesePurchase/CartinesePurchase'));
const Resume = lazy(() => import('./components/Resume/Resume'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy/PrivacyPolicy'));
const FilteredArtistPlaylist = lazy(() => import('./components/FilteredArtistPlaylist/FilteredArtistPlaylist'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const CreateArtistsSetsPlaylist = lazy(() => import('./components/CreateArtistsSetsPlaylist/CreateArtistsSetsPlaylist'));
const PlaylistAnalysis = lazy(() => import('./components/PlaylistAnalysis/PlaylistAnalysis'));
const PlaylistIntersection = lazy(() => import('./components/PlaylistIntersection/PlaylistIntersection'));
const PlaylistSegments = lazy(() => import('./components/PlaylistSegments/PlaylistSegments'));
const PokerTrainer = lazy(() => import('./components/PokerTrainer/PokerTrainer'));
const TransitPlanner = lazy(() => import('./components/TransitPlanner/TransitPlanner'));
const FireSimulator = lazy(() => import('./components/FireSimulator/FireSimulator'));
const RsuSimulator = lazy(() => import('./components/RsuSimulator/RsuSimulator'));
const LetterboxdIntersection = lazy(() => import('./components/LetterboxdIntersection/LetterboxdIntersection'));
const LetterboxdDiscovery = lazy(() => import('./components/LetterboxdDiscovery/LetterboxdDiscovery'));
const PastePage = lazy(() => import('./components/Paste/PastePage'));
const HealthDashboard = lazy(() => import('./components/HealthDashboard/HealthDashboard'));
const SiteStats = lazy(() => import('./components/SiteStats/SiteStats'));
const CitibikeExplorer = lazy(() => import('./components/Citibike/CitibikeExplorer'));
const Agent = lazy(() => import('./components/Agent/Agent'));
const CartiScope = lazy(() => import('./components/Cartinese/CartiScope'));

const CHAT_PATHS = ['/agent', '/chat', '/cartigpt', '/tweetgpt'];
const FULL_SCREEN_PATHS = ['/crime', '/crime-map', '/resume'];

function CartiRoute({ children }: { children: ReactNode }) {
    return <CartiScope>{children}</CartiScope>;
}

function Layout() {
    useAuthSession();
    const { pathname } = useLocation();
    // Codenames is its own little app — no site chrome, pure black canvas.
    const isCodenames =
      pathname === '/cn' ||
      pathname.startsWith('/cn/') ||
      pathname === '/codenames' ||
      pathname.startsWith('/codenames/');
    const isPastes = pathname === '/p' || pathname.startsWith('/p/');
    const hideShell = isCodenames || isPastes;
    // Chat pages fill the screen under the header, so a footer below only adds scroll.
    const isChat = CHAT_PATHS.includes(pathname.replace(/\/$/, ''));
    // A full-screen tool has nothing below it, so a footer only adds a page scroll.
    const isFullScreen = FULL_SCREEN_PATHS.includes(pathname.replace(/\/$/, ''));
    return (
        <AgentPanelProvider>
            <ShellProvider>
                <div
                    className={cn(
                        'flex min-h-screen flex-col lg:pr-[var(--agent-panel-width,0px)]',
                        hideShell ? 'bg-black' : 'page-background',
                        // Desktop Pastes fills the viewport. Phone Pastes scrolls with the page.
                        isPastes && 'lg:h-dvh lg:min-h-0 lg:overflow-hidden'
                    )}
                >
                    <a
                        href="#main"
                        className="sr-only z-[70] rounded-md bg-primary px-3 py-2 text-sm font-medium text-white focus:not-sr-only focus:fixed focus:left-3 focus:top-2"
                    >
                        Skip to Content
                    </a>
                    {!hideShell && <Header />}
                    <main id="main" tabIndex={-1} className="flex min-h-0 flex-1 flex-col outline-none">
                        <Suspense fallback={<div role="status" data-route-loading className="p-8 text-center text-muted-foreground">Loading…</div>}>
                            <Outlet />
                        </Suspense>
                    </main>
                    <RouteChangeTrackerWrapper />
                    <VisitorCounter hidden={hideShell || isChat || isFullScreen} />
                    <AgentBubble />
                </div>
            </ShellProvider>
        </AgentPanelProvider>
    );
}

/** Old /codenames links land on the short /cn path. */
function LegacyCodenamesRedirect({ role }: { role?: 'operative' | 'spymaster' }) {
    const { room } = useParams();
    const suffix = role === 'spymaster' ? '/sm' : role === 'operative' ? '/p' : '';
    return <Navigate to={room ? `/cn/${room}${suffix}` : '/cn'} replace />;
}

/** Old long product paths land on the one-word path. */
function LegacyRedirect({ to }: { to: string }) {
    const { search, hash } = useLocation();
    return <Navigate to={`${to}${search}${hash}`} replace />;
}

function LegacyFlightsRedirect({ defaultView }: { defaultView: FlightView }) {
    const { search, hash } = useLocation();
    return <Navigate to={legacyFlightsPath(search, hash, defaultView)} replace />;
}

const router = createBrowserRouter([
    {element: <Layout />,
     errorElement: <ErrorPage />,
     children: [
        { path: '/', element: <App /> },
        { path: '/product', element: <LegacyRedirect to="/" />},
        { path: '/pricing', element: <Navigate to="/cartinese/purchase" replace />},
        // User
        { path: '/login', element: <Login /> },
        { path: '/register', element: <Register /> },
        { path: '/password-reset', element: <PasswordReset /> },
        { path: '/verify-email', element: <VerifyEmail /> },
        { path: '/profile', element: <Profile /> },
        // Spotify
        { path: '/spotify', element: <Navigate to="/?shelf=music" replace />},
        { path: '/spotify-auth', element: <SpotifyAuth />},
        { path: '/discover', element: <SpotifyAccountBoundary><Spotify /></SpotifyAccountBoundary> },
        { path: '/events', element: <SpotifyAccountBoundary><FindEvents /></SpotifyAccountBoundary>},
        { path: '/set-playlist', element: <SpotifyAccountBoundary><SetPlaylist /></SpotifyAccountBoundary>},
        { path: '/sets', element: <SpotifyAccountBoundary><SetPlaylist /></SpotifyAccountBoundary>},
        { path: '/dupes', element: <SpotifyAccountBoundary><SpotifyDuplicates /></SpotifyAccountBoundary>},
        { path: '/artists', element: <SpotifyAccountBoundary><SpotifyCountArtists /></SpotifyAccountBoundary>},
        { path: '/fansince', element: <SpotifyAccountBoundary><FanSince /></SpotifyAccountBoundary>},
        { path: '/wrapped', element: <SpotifyAccountBoundary><ListeningStats /></SpotifyAccountBoundary>},
        { path: '/dj-sets-playlist', element: <SpotifyAccountBoundary><CreateArtistsSetsPlaylist /></SpotifyAccountBoundary>},
        { path: '/djsets', element: <SpotifyAccountBoundary><CreateArtistsSetsPlaylist /></SpotifyAccountBoundary>},
        { path: '/dna', element: <SpotifyAccountBoundary><PlaylistAnalysis /></SpotifyAccountBoundary>},
        { path: '/overlap', element: <SpotifyAccountBoundary><PlaylistIntersection /></SpotifyAccountBoundary>},
        { path: '/segments', element: <SpotifyAccountBoundary><PlaylistSegments /></SpotifyAccountBoundary>},
        // Twitter
        { path: '/twitter', element: <TweetGallery /> },
        { path: '/tweetgpt', element: <TwitterChat /> },
        // Gen AI
        { path: '/prompt', element: <PromptHelper /> },
        { path: '/chat', element: <Agent />},
        { path: '/cartigpt', element: <CartiRoute><CartiChat /></CartiRoute>},
        // Other
        { path: '/split-bills', element: <BillSplitter />},
        { path: '/split-bills/:id', element: <BillSplitter />},
        { path: '/bill-splitter', element: <BillSplitter />},
        { path: '/bill-splitter/:id', element: <BillSplitter />},
        { path: '/bills/:id', element: <BillSplitter />},
        { path: '/bills', element: <BillSplitter />},
        { path: '/split/session/:id', element: <BillSplitter /> },
        { path: '/split/:id', element: <BillSplitter /> },
        { path: '/split', element: <BillSplitter /> },
        { path: '/crime-map', element: <CrimeMap />},
        { path: '/crime', element: <CrimeMap />},
        { path: '/cn', element: <Codenames />},
        { path: '/cn/:room/sm/:team', element: <LegacyCodenamesRedirect role="spymaster" />},
        { path: '/cn/:room/sm', element: <Codenames role="spymaster" />},
        { path: '/cn/:room/p', element: <Codenames role="operative" />},
        { path: '/cn/:room', element: <Codenames />},
        { path: '/codenames', element: <Navigate to="/cn" replace />},
        { path: '/codenames/:room/sm/:team', element: <LegacyCodenamesRedirect role="spymaster" />},
        { path: '/codenames/:room/sm', element: <LegacyCodenamesRedirect role="spymaster" />},
        { path: '/codenames/:room/p', element: <LegacyCodenamesRedirect role="operative" />},
        { path: '/codenames/:room', element: <LegacyCodenamesRedirect />},
        { path: FLIGHT_VIEW_PATHS.stats, element: <RandomlySelected />},
        { path: FLIGHT_VIEW_PATHS.history, element: <RandomlySelected />},
        { path: FLIGHT_VIEW_PATHS.airports, element: <RandomlySelected />},
        { path: FLIGHT_VIEW_PATHS.selections, element: <RandomlySelected />},
        { path: '/flight-tracker', element: <LegacyFlightsRedirect defaultView="stats" />},
        // /ssss is the code printed on a boarding pass when you are picked for
        // secondary screening. It was the original path and is still linked to
        // from outside, so it and the other old names keep working.
        { path: '/ssss', element: <LegacyFlightsRedirect defaultView="selections" />},
        { path: '/randomly-selected', element: <LegacyFlightsRedirect defaultView="selections" />},
        { path: '/selected', element: <LegacyFlightsRedirect defaultView="selections" />},
        { path: '/poker', element: <PokerTrainer />},
        { path: '/poker-trainer', element: <PokerTrainer />},
        { path: '/mta', element: <TransitPlanner />},
        { path: '/departure', element: <TransitPlanner />},
        { path: '/fire', element: <FireSimulator />},
        { path: '/fire-simulator', element: <FireSimulator />},
        { path: '/rsu', element: <RsuSimulator />},
        { path: '/rsu-simulator', element: <RsuSimulator />},
        { path: '/letterboxd', element: <LetterboxdIntersection />},
        { path: '/watchlists', element: <LetterboxdIntersection />},
        { path: '/movies', element: <LetterboxdDiscovery />},
        { path: '/health', element: <HealthDashboard />},
        { path: '/stats', element: <SiteStats />},
        { path: '/citibike', element: <CitibikeExplorer />},
        {
            path: '/p',
            element: <PastePage />,
            children: [
                { index: true },
                {
                    path: ':pasteId',
                    id: 'paste',
                    loader: pasteLoader,
                },
            ],
        },
        { path: '/cartinese', element: <CartiRoute><Cartinese /></CartiRoute>},
        { path: '/cartinese/purchase', element: <CartiRoute><CartinesePurchase /></CartiRoute>},
        { path: '/filter', element: <SpotifyAccountBoundary><FilteredArtistPlaylist /></SpotifyAccountBoundary>},
        // Old long paths. Spotify playlist descriptions and shared links still use them.
        { path: '/find-events', element: <LegacyRedirect to="/events" />},
        { path: '/find-new-music', element: <LegacyRedirect to="/discover" />},
        { path: '/find-duplicates', element: <LegacyRedirect to="/dupes" />},
        { path: '/count-artists', element: <LegacyRedirect to="/artists" />},
        { path: '/fan-since', element: <LegacyRedirect to="/fansince" />},
        { path: '/filter-playlist', element: <LegacyRedirect to="/filter" />},
        { path: '/playlist-analysis', element: <LegacyRedirect to="/dna" />},
        { path: '/playlist-intersection', element: <LegacyRedirect to="/overlap" />},
        { path: '/playlist-segments', element: <LegacyRedirect to="/segments" />},
        { path: '/sr-ai', element: <LegacyRedirect to="/chat" />},
        { path: '/twitter-chat', element: <LegacyRedirect to="/tweetgpt" />},
        { path: '/prompt-helper', element: <LegacyRedirect to="/prompt" />},
        { path: '/discover-movies', element: <LegacyRedirect to="/movies" />},
        { path: '/carti-chat', element: <LegacyRedirect to="/cartigpt" />},
        // Resume
        { path: '/resume', element: <Resume />},
        // Terms and Conditions
        { path: '/terms', element: <TermsAndConditions />},
        { path: '/privacy', element: <PrivacyPolicy />},
        // Admin
        { path: '/dashboard', element: <Dashboard />},
        { path: '/agent', element: <Agent />},
     ]},
]);

export default router;
